// STEP MINI-ORDER-V1 · Consumer MINI (web).
//
// 역할: LIVE Session 의 현재 소개 상품 **+ 전체 판매상품** 을 모바일에서 1-tap
// 으로 주문할 수 있는 경량 UI.  별도 쇼핑몰이 아니다 — 기존 live-context polling
// + OptionSheet + inline-order endpoint 를 그대로 REUSE 한다.
//
// 데이터 흐름:
//   GET /api/public/streaming/sessions/{sessionId}/live-context  (3s polling)
//     → active_product · live_products · seller · streaming_status · platform
//     → active_product.size_table 로 사이즈표 노출 (ProductSizeTable REUSE)
//
// 상품 선택 분리 (지시 §MINI-UI-1 B2 — "주문 대상이 잘못 변경되지 않도록"):
//   · `selectedProductId` state 로 소비자가 명시 선택한 상품 추적.
//   · displayed = (selected ? live_products[selected] : active_product).
//   · UI 상단에 "LIVE 소개 중" / "선택한 상품" 명시 badge 노출 — 혼동 방지.
//   · OptionSheet 는 displayed 를 기반으로 주문, attribution.source_product_id
//     도 displayed 를 그대로 사용 (fabrication 금지).
//
// 주문:
//   OptionSheet (ProductDetail 과 완전히 동일) 가 product.broadcast_id 를 보고
//   /public/streaming/sessions/{sid}/inline-order 호출.  attribution={
//     source_type:'mini_order', source_session_id, source_platform, source_product_id
//   } 를 추가로 전달하여 canonical create_order 에 그대로 보존.
//
// 금지 (STEP 지시 §9/§15):
//   · 신규 realtime infrastructure (polling 만 사용)
//   · 신규 order/inventory endpoint
//   · PG 결제 provider 직접 호출 (결제는 별도 화면 · 외부 provider HOLD)
//   · product detail 전체 복제 (size_table 는 tap-expand 로 compact 노출)
//   · seller 전체 상품 노출 (해당 세션 broadcast_products 만 — §지시서 추가 조건)
//
// Android WebView / iOS WebView 에서 그대로 재사용 가능하도록 top-level
// Container 는 100vw / dvh 단위로 작성 · 외부 navigation 의존 최소화.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingBag, Store, Radio, Package, Ruler, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import OptionSheet from '@/components/OptionSheet';
import ProductSizeTable from '@/components/product/ProductSizeTable';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const PLATFORM_LABEL = {
  youtube:   'YouTube LIVE',
  instagram: 'Instagram LIVE',
  tiktok:    'TikTok LIVE',
  livemoa:   'LIVEMOA LIVE',
  naver:     'Naver LIVE',
};

const ConsumerMini = () => {
  // STEP MINI-EXTERNAL-SESSION-IMPL-1 · 2 route 모드 지원.
  //   · /live/:sessionId/mini   → LIVEMOA Studio 송출 (streaming_sessions 기반)
  //   · /live-mini/:scheduleId  → 외부 SNS (live_schedules 기반, 영상 송출 X)
  // useParams 는 매칭된 라우트 param 만 반환 — 두 모드를 자동 분기.
  const { sessionId, scheduleId } = useParams();
  const isExternalMode = !!scheduleId;
  const contextId = scheduleId || sessionId;
  const [ctx, setCtx] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showSize, setShowSize] = useState(false);
  // STEP MINI-ORDER-V1 B2 · 소비자가 명시 선택한 상품 ID.
  // null = active_product (LIVE 소개 중) 자동 표시.
  // 설정 시 live_products 안에서 해당 상품을 찾아 displayed 로 사용.
  const [selectedProductId, setSelectedProductId] = useState(null);

  // 3s polling · 모드별 endpoint 자동 분기.
  //   · session mode: /public/streaming/sessions/{sid}/live-context (기존)
  //   · schedule mode: /public/broadcasts/{sched}/mini-context (신규 · 외부 전용)
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const endpoint = isExternalMode
      ? `/public/broadcasts/${contextId}/mini-context`
      : `/public/streaming/sessions/${contextId}/live-context`;
    const fetchOnce = async () => {
      try {
        const res = await api.get(endpoint);
        if (!cancelled) {
          setCtx(res.data || null);
          setNotFound(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
        }
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, 3000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [contextId, isExternalMode]);

  const active = ctx?.active_product || null;
  const liveProducts = Array.isArray(ctx?.live_products) ? ctx.live_products : [];
  const platform = ctx?.platform || null;
  const streamingStatus = ctx?.streaming_status || null;
  const seller = ctx?.seller || null;
  const ended = streamingStatus && streamingStatus !== 'live';

  // STEP MINI-ORDER-V1 B2 · displayed = 명시 선택 > active_product.
  // 선택 상품이 live_products 에서 사라졌다 (판매자 삭제) 면 자동 fallback.
  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return liveProducts.find((p) => p.product_id === selectedProductId) || null;
  }, [selectedProductId, liveProducts]);
  const displayed = selectedProduct || active;
  // 분리 — "LIVE 소개 중" vs "선택한 상품". OptionSheet 와 bottom CTA 가 모두 이 값 사용.
  const isSelectedView = !!selectedProduct;

  // OptionSheet 는 product.broadcast_id 가 있을 때 inline-order 로 자동 routing.
  // STEP MINI-EXTERNAL-SESSION-IMPL-1 · 외부 모드는 schedule-based 신규 endpoint
  // 로 라우팅 (OptionSheet 에 orderEndpoint prop 주입).
  // size_table 은 ProductSizeTable 로 compact 노출 (tap 확장).
  const productForSheet = useMemo(() => {
    if (!displayed) return null;
    return {
      product_id:     displayed.product_id,
      seller_id:      seller?.seller_id || displayed.seller_id,
      name:           displayed.name,
      image:          displayed.image,
      sale_price:     displayed.sale_price,
      original_price: displayed.original_price,
      discount_rate:  displayed.discount_rate,
      options:        displayed.options || [],
      shipping_option: displayed.shipping_option,
      shipping_policy: displayed.shipping_policy,
      // OptionSheet 가 이 값을 보고 /inline-order 로 routing (ProductDetail 과 동일).
      broadcast_id:   contextId,
    };
  }, [displayed, seller?.seller_id, contextId]);

  // 외부 모드 — schedule-based mini-order endpoint 사용.
  const orderEndpoint = useMemo(() => (
    isExternalMode ? `/public/broadcasts/${contextId}/mini-order` : null
  ), [isExternalMode, contextId]);

  // mini_order attribution — canonical create_order 가 그대로 orders.attribution 에 저장.
  // source_product_id 는 **실제 주문되는 상품** 을 반영 (선택 상품 vs active_product).
  // STEP MINI-EXTERNAL-SESSION-IMPL-1 · 모드별 source_session_id / source_schedule_id 상호배타.
  const miniAttribution = useMemo(() => {
    if (!displayed?.product_id) return null;
    return {
      source_type:        'mini_order',
      source_session_id:  isExternalMode ? null : contextId,
      source_schedule_id: isExternalMode ? contextId : null,
      source_platform:    platform || null,
      source_product_id:  displayed.product_id,
    };
  }, [displayed?.product_id, contextId, platform, isExternalMode]);

  // ─── Error / empty states ──────────────────────────────────────────────-

  if (notFound) {
    return (
      <SafeShell>
        <EmptyState icon={Radio} testid="mini-session-notfound"
                     title="방송을 찾을 수 없어요"
                     subtitle="주소를 다시 확인해 주세요." />
      </SafeShell>
    );
  }

  if (!ctx) {
    return (
      <SafeShell>
        <div className="pt-20 text-center text-sm text-gray-400"
              data-testid="mini-loading">불러오는 중…</div>
      </SafeShell>
    );
  }

  if (ended) {
    return (
      <SafeShell>
        <SellerHeader seller={seller} platform={platform} ended />
        <EmptyState icon={Radio} testid="mini-ended"
                     title="방송이 종료되었습니다"
                     subtitle="곧 다음 LIVE 로 다시 만나요." />
      </SafeShell>
    );
  }

  // active_product 와 live_products 가 모두 비어있을 때만 최종 Empty.
  if (!displayed && liveProducts.length === 0) {
    return (
      <SafeShell>
        <SellerHeader seller={seller} platform={platform} />
        <EmptyState icon={Package} testid="mini-no-active-product"
                     title="현재 소개 중인 상품이 없습니다"
                     subtitle="판매자가 상품을 소개하면 자동으로 표시됩니다." />
      </SafeShell>
    );
  }

  // ─── Main: Active (or Selected) Product + Order CTA + Product List ────────

  const hasSizeTable = !!(displayed?.size_table
    && ((displayed.size_table.columns || []).length
        || (displayed.size_table.rows || []).length
        || (displayed.size_table.guide_text || '').trim()));

  return (
    <SafeShell>
      <SellerHeader seller={seller} platform={platform} />

      {displayed ? (
        <div className="px-4 pb-28 pt-2" data-testid="mini-active-product">
          {/* 선택 vs LIVE 소개 중 명시 badge — 혼동 방지. */}
          <div className="flex items-center justify-between mb-2">
            {isSelectedView ? (
              <>
                <button type="button" onClick={() => setSelectedProductId(null)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500"
                        data-testid="mini-selected-back">
                  <ArrowLeft size={12} /> LIVE 소개 상품으로
                </button>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black text-white dark:bg-white dark:text-black text-[10px] font-black"
                       data-testid="mini-selected-badge">
                  선택한 상품
                </span>
              </>
            ) : (
              <>
                <span className="text-[11px] font-bold text-gray-500">현재 소개 상품</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
                       data-testid="mini-live-active-badge">
                  LIVE 소개 중
                </span>
              </>
            )}
          </div>

          <div className="rounded-2xl overflow-hidden bg-black/[0.03] dark:bg-white/[0.04] aspect-square mb-3"
                data-testid="mini-product-image"
                data-view-mode={isSelectedView ? 'selected' : 'active'}>
            {displayed.image ? (
              <SafeImg src={displayed.image} kind="product" alt={displayed.name}
                        className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <Package size={36} />
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 mb-1">
            {displayed.slot_number ? (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black"
                     data-testid="mini-product-slot">{displayed.slot_number}</span>
            ) : null}
            <h1 className="text-xl font-black leading-tight flex-1"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="mini-product-name">{displayed.name}</h1>
          </div>

          <div className="flex items-baseline gap-2 mb-3">
            {displayed.discount_rate > 0 && (
              <>
                <span className="text-xl font-black text-[#FF3B30]"
                       data-testid="mini-product-discount">{displayed.discount_rate}%</span>
                <span className="text-xs text-gray-400 line-through"
                       data-testid="mini-product-original">{won(displayed.original_price)}</span>
              </>
            )}
            <span className="ml-auto text-xl font-black"
                   data-testid="mini-product-price">{won(displayed.sale_price)}</span>
          </div>

          <StockBadge stock={displayed.available_stock} status={displayed.stock_status} />

          {hasSizeTable && (
            <div className="mt-4" data-testid="mini-size-table-section">
              <button type="button" onClick={() => setShowSize((v) => !v)}
                      className="w-full inline-flex items-center justify-between px-3 py-2.5 rounded-xl border border-black/10 dark:border-white/10 text-sm font-black"
                      data-testid="mini-size-table-toggle">
                <span className="inline-flex items-center gap-2"><Ruler size={14} /> 사이즈 안내</span>
                <span className="text-xs text-gray-500">{showSize ? '닫기' : '보기'}</span>
              </button>
              {showSize && (
                <div className="-mt-6" data-testid="mini-size-table-expanded">
                  <ProductSizeTable sizeTable={displayed.size_table} />
                </div>
              )}
            </div>
          )}

          {/* 전체 판매상품 목록 — 해당 세션 broadcast_products 만.
              seller 전체 상품 금지 (지시 §MINI-UI-1 추가 조건). */}
          {liveProducts.length > 1 && (
            <div className="mt-6" data-testid="mini-live-products-section">
              <h2 className="text-[13px] font-black text-gray-800 dark:text-gray-200 mb-2"
                  data-testid="mini-live-products-title">전체 판매상품 ({liveProducts.length})</h2>
              <div className="grid grid-cols-2 gap-2" data-testid="mini-live-products-grid">
                {liveProducts.map((p) => {
                  const isCurrent = p.product_id === displayed?.product_id;
                  const isActive = p.product_id === active?.product_id;
                  const soldOut = p.stock_status === 'sold_out';
                  return (
                    <button key={p.product_id} type="button"
                            disabled={isCurrent}
                            onClick={() => {
                              // 명시 선택 — active 와 같은 상품을 눌렀을 때는 selected 해제.
                              if (p.product_id === active?.product_id) {
                                setSelectedProductId(null);
                              } else {
                                setSelectedProductId(p.product_id);
                              }
                              // 상단으로 scroll (compact 상세 교체).
                              try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                            }}
                            className={`text-left rounded-xl overflow-hidden border transition disabled:opacity-100 ${
                              isCurrent
                                ? 'border-[#FF3B30] shadow-sm ring-1 ring-[#FF3B30]/30'
                                : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/60'
                            }`}
                            data-testid={`mini-product-card-${p.product_id}`}
                            data-current={isCurrent ? '1' : '0'}
                            data-active-live={isActive ? '1' : '0'}>
                      <div className="aspect-square bg-black/[0.03] dark:bg-white/[0.04] relative">
                        {p.image ? (
                          <SafeImg src={p.image} kind="product" alt=""
                                   className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                            <Package size={20} />
                          </div>
                        )}
                        {soldOut && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="px-2 py-1 rounded-full bg-white/90 text-black text-[10px] font-black"
                                  data-testid={`mini-product-card-soldout-${p.product_id}`}>품절</span>
                          </div>
                        )}
                        {isActive && (
                          <span className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black"
                                data-testid={`mini-product-card-live-${p.product_id}`}>
                            <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="text-[11px] font-bold text-black dark:text-white truncate"
                           data-testid={`mini-product-card-name-${p.product_id}`}>{p.name}</p>
                        <p className="text-[12px] font-black text-[#FF3B30] mt-0.5"
                           data-testid={`mini-product-card-price-${p.product_id}`}>{won(p.sale_price)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <EmptyState icon={Package} testid="mini-no-active-product"
                     title="현재 소개 중인 상품이 없습니다"
                     subtitle="아래 판매상품에서 선택해 주세요." />
      )}

      {/* Bottom bar · 주문 CTA.  OptionSheet 가 /inline-order 로 자동 routing. */}
      {displayed && (
        <div className="fixed inset-x-0 bottom-0 z-30 bg-white dark:bg-[#0A0A0A] border-t border-black/10 dark:border-white/10 px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]"
              data-testid="mini-bottom-bar">
          <button type="button"
                  onClick={() => setSheetOpen(true)}
                  className="w-full py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-base font-black inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  data-testid="mini-order-cta"
                  data-order-mode={isSelectedView ? 'selected' : 'active'}
                  disabled={displayed.stock_status === 'sold_out'}>
            <ShoppingBag size={16} />
            {displayed.stock_status === 'sold_out'
              ? '품절'
              : (isSelectedView ? '선택 상품 주문하기' : '주문하기')}
          </button>
        </div>
      )}

      <OptionSheet product={productForSheet}
                    open={sheetOpen}
                    intent="buy"
                    onClose={() => setSheetOpen(false)}
                    attribution={miniAttribution}
                    orderEndpoint={orderEndpoint} />
    </SafeShell>
  );
};

// ─── Small building blocks (same file; MINI 는 compact UI 전제) ────────────

const SafeShell = ({ children }) => (
  <div className="min-h-[100dvh] bg-white dark:bg-[#0A0A0A] text-black dark:text-white max-w-md mx-auto"
        style={{ overflowX: 'hidden' }}
        data-testid="mini-shell">
    {children}
  </div>
);

const SellerHeader = ({ seller, platform, ended = false }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-black/5 dark:border-white/10 sticky top-0 bg-white/90 dark:bg-[#0A0A0A]/90 backdrop-blur-sm z-20"
        data-testid="mini-seller-header">
    <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
      {seller?.profile_photo ? (
        <SafeImg src={seller.profile_photo} kind="profile" alt=""
                  className="w-full h-full object-cover" />
      ) : <div className="w-full h-full inline-flex items-center justify-center text-gray-400"><Store size={14} /></div>}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-black truncate"
          data-testid="mini-seller-name">{seller?.name || '판매자'}</p>
      <p className="text-[10px] text-gray-500 truncate"
          data-testid="mini-platform-badge">
        {ended ? '방송 종료' : (platform && PLATFORM_LABEL[platform]) || 'LIVE'}
      </p>
    </div>
    {!ended && (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
             data-testid="mini-live-badge">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
      </span>
    )}
  </div>
);

const StockBadge = ({ stock, status }) => {
  if (status === 'sold_out') {
    return <span className="inline-block px-2 py-1 rounded-full text-[11px] font-black bg-gray-200 dark:bg-white/10 text-gray-500"
                   data-testid="mini-stock-sold-out">SOLD OUT</span>;
  }
  if (status === 'low_stock') {
    return <span className="inline-block px-2 py-1 rounded-full text-[11px] font-black bg-[#FF3B30]/10 text-[#FF3B30]"
                   data-testid="mini-stock-low">품절 임박</span>;
  }
  return null;
};

const EmptyState = ({ icon: Icon, title, subtitle, testid }) => (
  <div className="pt-20 pb-8 flex flex-col items-center text-center px-6"
        data-testid={testid}>
    <Icon size={28} className="text-gray-400 mb-3" />
    <p className="text-base font-black">{title}</p>
    {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
  </div>
);

export default ConsumerMini;
